import React, { useEffect, useMemo, useState } from "react";
import FiltroMultiple from "../../../Postventa/Dashboard/Modales/FiltroMultiple";

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const CAMPOS_MES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

// "giros" llega como un solo string con varios giros separados por coma (ej. "ALIMENTOS Y
// BEBIDAS, Ganaderia, Maquinaria Agrícola") — esto lo separa en un array limpio para poder
// filtrar por cada giro individual, no por el string completo.
const parseGiros = (giros) => (giros || "").split(",").map((g) => g.trim()).filter(Boolean);

const ESTADO_VACIO_FILTROS = { asesor: [], razonSocial: [], sucursal: [], estado: [], giro: [], localidad: [] };
const ESTADO_VACIO_MOSTRAR = {
  asesor: { open: false },
  razonSocial: { open: false },
  sucursal: { open: false },
  estado: { open: false },
  giro: { open: false },
  localidad: { open: false },
};

// Tabla agrupada por asesor — mismo patrón que ReporteClientesTabla (Información Capturada).
// "listado" llega plano desde ReportesCRM/ReporteCoberturaVisitas — cada fila es un cliente
// con idcliente, razon_social, idvendedor, vendedor, idsucursal, sucursal, idestado, estado,
// idlocalidad, localidad, giros, enero..diciembre y total_visitas (no se muestran los ids).
export const ReporteCoberturaVisitasTabla = ({ listado: listadoProp }) => {
  const listado = Array.isArray(listadoProp) ? listadoProp : [];
  const [options, setOptions] = useState(ESTADO_VACIO_FILTROS);
  const [filtros, setFiltros] = useState(ESTADO_VACIO_FILTROS);
  const [filtrosTemp, setFiltrosTemp] = useState(ESTADO_VACIO_FILTROS);
  const [mostrarFiltro, setMostrarFiltro] = useState(ESTADO_VACIO_MOSTRAR);
  const [prioridadFiltros, setPrioridadFiltros] = useState([]);
  const [abiertos, setAbiertos] = useState({});

  const onToggle = (idvendedor) => {
    setAbiertos((prev) => ({ ...prev, [idvendedor]: !prev[idvendedor] }));
  };

  useEffect(() => {
    setOptions({
      asesor: [...new Set(listado.map((c) => c.vendedor).filter(Boolean))].map((v) => ({ value: v, label: v })),
      razonSocial: [...new Set(listado.map((c) => c.razon_social).filter(Boolean))].map((v) => ({ value: v, label: v })),
      sucursal: [...new Set(listado.map((c) => c.sucursal).filter(Boolean))].map((v) => ({ value: v, label: v })),
      estado: [...new Set(listado.map((c) => c.estado).filter(Boolean))].map((v) => ({ value: v, label: v })),
      giro: [...new Set(listado.flatMap((c) => parseGiros(c.giros)))].sort((a, b) => a.localeCompare(b, "es")).map((v) => ({ value: v, label: v })),
      localidad: [...new Set(listado.map((c) => c.localidad).filter(Boolean))].map((v) => ({ value: v, label: v })),
    });
  }, [listado]);

  const actualizarPrioridad = (filtro) => {
    setPrioridadFiltros((prev) => {
      const index = prev.indexOf(filtro);
      const nuevasPrioridades = index === -1 ? [...prev, filtro] : prev.slice(0, index + 1);

      setFiltrosTemp((prevTemp) => {
        const limpio = { ...prevTemp };
        Object.keys(limpio).forEach((key) => {
          if (!nuevasPrioridades.includes(key)) limpio[key] = [];
        });
        return limpio;
      });

      setFiltros((prevFiltros) => {
        const limpio = { ...prevFiltros };
        Object.keys(limpio).forEach((key) => {
          if (!nuevasPrioridades.includes(key)) limpio[key] = [];
        });
        return limpio;
      });

      return nuevasPrioridades;
    });
  };

  const asesoresAgrupados = useMemo(() => {
    let data = [...listado];

    prioridadFiltros.forEach((filtro) => {
      if (filtro === "asesor" && filtros.asesor.length > 0) {
        data = data.filter((c) => filtros.asesor.includes(c.vendedor));
      }
      if (filtro === "razonSocial" && filtros.razonSocial.length > 0) {
        data = data.filter((c) => filtros.razonSocial.includes(c.razon_social));
      }
      if (filtro === "sucursal" && filtros.sucursal.length > 0) {
        data = data.filter((c) => filtros.sucursal.includes(c.sucursal));
      }
      if (filtro === "estado" && filtros.estado.length > 0) {
        data = data.filter((c) => filtros.estado.includes(c.estado));
      }
      if (filtro === "giro" && filtros.giro.length > 0) {
        data = data.filter((c) => parseGiros(c.giros).some((g) => filtros.giro.includes(g)));
      }
      if (filtro === "localidad" && filtros.localidad.length > 0) {
        data = data.filter((c) => filtros.localidad.includes(c.localidad));
      }
    });

    const grupos = new Map();
    data.forEach((c) => {
      if (!grupos.has(c.idvendedor)) {
        grupos.set(c.idvendedor, {
          idvendedor: c.idvendedor,
          vendedor: c.vendedor,
          clientes: [],
          totalVisitas: 0,
          totalesMes: CAMPOS_MES.reduce((acc, m) => ({ ...acc, [m]: 0 }), {}),
        });
      }
      const grupo = grupos.get(c.idvendedor);
      grupo.clientes.push(c);
      grupo.totalVisitas += c.total_visitas || 0;
      CAMPOS_MES.forEach((m) => {
        grupo.totalesMes[m] += c[m] || 0;
      });
    });

    const esSinAsesor = (idvendedor) => idvendedor === -1 || idvendedor === 0;
    return [...grupos.values()].sort((a, b) => {
      const aSinAsesor = esSinAsesor(a.idvendedor);
      const bSinAsesor = esSinAsesor(b.idvendedor);
      if (aSinAsesor !== bSinAsesor) return aSinAsesor ? 1 : -1;
      return (a.vendedor || "").localeCompare(b.vendedor || "", "es");
    });
  }, [listado, filtros, prioridadFiltros]);

  const totalClientes = asesoresAgrupados.reduce((acc, a) => acc + a.clientes.length, 0);
  const totalVisitas = asesoresAgrupados.reduce((acc, a) => acc + a.totalVisitas, 0);

  const filtroProps = (field, label) => ({
    label,
    field,
    options: options[field],
    filtros,
    setFiltros,
    filtrosTemp,
    setFiltrosTemp,
    mostrarFiltro,
    setMostrarFiltro,
    actualizarPrioridad,
    setPrioridadFiltros,
  });

  return (
    <div className="crmcs-cot-tabla-wrap">
      <div className="crmcs-cot-tabla-scroll scroll">
        <table cellPadding={0} cellSpacing={0} className="rcv-tabla">
          <thead>
            <tr>
              <th>
                <div>
                  <FiltroMultiple {...filtroProps("asesor", "Asesor")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("razonSocial", "Cliente")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("sucursal", "Sucursal")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("estado", "Estado")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("giro", "Giro")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("localidad", "Localidad")} />
                </div>
              </th>
              {MESES.map((m) => (
                <th key={m} className="rcv-th-mes">
                  <div className="borde-izq">{m}</div>
                </th>
              ))}
              <th>
                <div className="borde-izq">Nº visitas</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {asesoresAgrupados.map((asesor) => {
              const expandido = !!abiertos[asesor.idvendedor];
              return (
                <React.Fragment key={asesor.idvendedor}>
                  <tr className="rca-row-asesor" onClick={() => onToggle(asesor.idvendedor)}>
                    <td className="rca-td-asesor">
                      <span className="rca-flecha">{expandido ? "▼" : "▶"}</span>
                      <strong>{asesor.vendedor}</strong>
                    </td>
                    <td colSpan={5}></td>
                    {CAMPOS_MES.map((m) => (
                      <td key={m} className="rca-td-contador rcv-td-mes">{asesor.totalesMes[m]}</td>
                    ))}
                    <td className="rca-td-contador rcv-td-total">
                      {asesor.totalVisitas} visitas
                    </td>
                  </tr>

                  {expandido && asesor.clientes.map((c) => (
                    <tr key={c.idcliente} className="rca-row-cliente">
                      <td></td>
                      <td className="rca-td-cliente">{c.razon_social}</td>
                      <td>{c.sucursal || "—"}</td>
                      <td>{c.estado || "—"}</td>
                      <td>{c.giros || "—"}</td>
                      <td>{c.localidad || "—"}</td>
                      {CAMPOS_MES.map((m) => (
                        <td key={m} className="rcv-td-mes">{c[m] || 0}</td>
                      ))}
                      <td className="rcv-td-total">{c.total_visitas || 0}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            <tr className="crmcs-fila-total-sticky">
              <td colSpan={2}>Total ({asesoresAgrupados.length} asesores · {totalClientes} clientes)</td>
              <td colSpan={4 + MESES.length}></td>
              <td>{totalVisitas} visitas</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
